import * as React from 'react';
import {alpha, styled} from '@mui/material/styles';
import MuiButton, {ButtonProps as MuiButtonProps} from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import {overlayColor} from '../utils/colors';
import {makeSxStyles} from '../utils/sx-helpers';

export enum ButtonHeight {
  SHORT = 'short',
  NORMAL = 'normal',
  MEDIUM = 'medium',
  TALL = 'tall',
}

export enum ButtonWidth {
  NARROW = 'narrow',
  MEDIUM = 'medium',
  WIDE = 'wide',
  ELASTIC = 'elastic',
  FULL_WIDTH = 'fullWidth',
}
type ButtonSize = 'small' | 'medium' | 'large';
const heightMap: Record<ButtonHeight, ButtonSize> = {
  [ButtonHeight.SHORT]: 'small',
  [ButtonHeight.NORMAL]: 'medium',
  [ButtonHeight.MEDIUM]: 'medium',
  [ButtonHeight.TALL]: 'large',
};

const widthMap: Record<ButtonWidth, string> = {
  [ButtonWidth.NARROW]: '64px',
  [ButtonWidth.MEDIUM]: '104px',
  [ButtonWidth.WIDE]: '152px',
  [ButtonWidth.FULL_WIDTH]: '100%',
  [ButtonWidth.ELASTIC]: 'auto',
};

export enum ButtonVariant {
  text = 'text',
  outlined = 'outlined',
  contained = 'contained',
}

const muiColors = ['primary', 'secondary'] as const;
type MuiColor = (typeof muiColors)[number];
const nonMuiColors = ['error', 'success', 'warning'] as const;
type NonMuiColor = (typeof nonMuiColors)[number];

type ButtonColor =
  | undefined
  | 'inherit'
  | 'primary'
  | 'secondary'
  | NonMuiColor;

const parseMuiColor = (color: ButtonColor): MuiColor | undefined => {
  return muiColors.includes(color as MuiColor)
    ? (color as MuiColor)
    : undefined;
};

const getBackgroundForBlank = alpha;
const getBackgroundForFilled = (
  base: string,
  overlay: string,
  opacity: number,
): string => overlayColor(base, alpha(overlay, opacity));

const bgFunctions = {
  text: getBackgroundForBlank,
  outlined: getBackgroundForBlank,
  contained: getBackgroundForFilled,
};

const focusOpacity = 0.24;

type ButtonProps = Omit<MuiButtonProps, 'variant' | 'color'> & {
  height?: ButtonHeight;
  width?: ButtonWidth;
  variant?: ButtonVariant;
  color?: ButtonColor;
  loading?: boolean;
  uppercase?: boolean;
  /** When not specified, spinner color matches button color */
  loadingIndicatorColor?: React.ComponentProps<
    typeof CircularProgress
  >['color'];
};

const StyledButton = styled(MuiButton)<ButtonProps>(({width}) => ({
  transition: 'all 0.75s ease',
  width: widthMap[width as keyof typeof widthMap],
}));

const makeUseStyles = ({color, variant}: ButtonProps) => {
  return makeSxStyles(theme => {
    const hasThemeColor = color && color !== 'inherit';
    const colorArgs = {
      text: hasThemeColor
        ? [theme.palette[color].main]
        : [theme.palette.text.primary],
      outlined: hasThemeColor
        ? [theme.palette[color].main]
        : [theme.palette.text.primary],
      contained: hasThemeColor
        ? [theme.palette[color].main, theme.palette[color].contrastText]
        : [
            theme.palette.grey[300],
            theme.palette.getContrastText(theme.palette.grey[300]),
          ],
    };

    const styleColors = colorArgs[variant || 'contained'];
    const hoverBgArgs = [...styleColors, theme.palette.action.hoverOpacity];
    const focusBgArgs = [...styleColors, focusOpacity];

    const bgFn = bgFunctions[variant || 'contained'];
    // @ts-ignore
    const hoverBg = (() => bgFn(...hoverBgArgs))();
    // @ts-ignore
    const focusBg = (() => bgFn(...focusBgArgs))();

    return {
      text: {
        color: hasThemeColor
          ? theme.palette[color].main
          : theme.palette.text.primary,
        '&:hover': {backgroundColor: hoverBg},
        '@media (hover: none)': {
          ':not(#\\20)&:hover': {backgroundColor: hoverBg},
        },
        '&:focus': {backgroundColor: focusBg},
      },
      outlined: {
        borderColor: hasThemeColor
          ? theme.palette[color].main
          : alpha(theme.palette.common.black, 0.2),
        color: hasThemeColor
          ? theme.palette[color].main
          : theme.palette.text.primary,
        '&:hover': {backgroundColor: hoverBg},
        '@media (hover: none)': {
          ':not(#\\20)&:hover': {backgroundColor: hoverBg},
        },
        '&:focus': {backgroundColor: focusBg},
      },
      contained: {
        backgroundColor: hasThemeColor ? theme.palette[color].main : 'inherit',
        color: hasThemeColor ? theme.palette[color].contrastText : 'inherit',
        '&:hover': {backgroundColor: hoverBg},
        '@media (hover: none)': {
          ':not(#\\20)&:hover': {backgroundColor: hoverBg},
        },
        '&:focus': {backgroundColor: focusBg},
      },
      noTransform: {textTransform: 'none'},
      spinnerContainer: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      },
    };
  });
};

const HideButtonText = styled('span')({visibility: 'hidden'});

const Spinner: React.FC<{
  color?: 'inherit' | 'primary' | 'secondary';
}> = props => <CircularProgress size={24} {...props} />;

export const Button: React.FC<ButtonProps> = React.forwardRef(
  (
    {
      height = ButtonHeight.NORMAL,
      width = ButtonWidth.ELASTIC,
      variant = ButtonVariant.contained,
      loading,
      loadingIndicatorColor,
      disabled,
      children,
      color,
      uppercase = true,
      ...otherProps
    },
    ref,
  ) => {
    const colorAsMui = parseMuiColor(color);

    const [styles, sn] = makeUseStyles({
      color,
      variant,
    })();

    const ButtonTextWrapper = loading ? HideButtonText : React.Fragment;

    return (
      <StyledButton
        ref={ref}
        size={heightMap[height] as ButtonSize}
        width={width}
        disableFocusRipple
        disableRipple
        disabled={disabled || loading}
        data-loading={loading}
        // @ts-ignore
        variant={ButtonVariant[variant]}
        sx={sn(`${ButtonVariant[variant]}`, {
          noTransform: !uppercase,
        })}
        color={color || 'primary'}
        {...otherProps}
      >
        <ButtonTextWrapper>{children}</ButtonTextWrapper>
        {loading && (
          <Box sx={styles.spinnerContainer}>
            <Spinner
              data-testid="Button__Spinner"
              // @ts-ignore
              color={loadingIndicatorColor ?? colorAsMui ?? 'inherit'}
            />
          </Box>
        )}
      </StyledButton>
    );
  },
);
